// Phase B.3 — Admin Schedule Manager: prayer slots CRUD.
//
// Each slot has: label, HH:MM time (America/Chicago), days_of_week (0..6),
// optional track_id (must be approved), active flag, notes. Track-attached
// slots auto-play at the scheduled time on every listener (next clean break).
// Label-only slots only appear on the station clock.

import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import {
  CalendarClock, Plus, Trash2, Save, X, ToggleLeft, ToggleRight, AlertCircle, Check,
} from "lucide-react";

const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function DayPicker({ value, onChange, idPrefix }) {
  const set = new Set(value || []);
  const toggle = (n) => {
    if (set.has(n)) set.delete(n); else set.add(n);
    onChange(Array.from(set).sort((a, b) => a - b));
  };
  return (
    <div className="flex gap-1 flex-wrap" data-testid={`${idPrefix}-days`}>
      {DAY_LABELS.map((lbl, i) => {
        const on = set.has(i);
        return (
          <button
            key={i}
            type="button"
            onClick={() => toggle(i)}
            className={`text-[11px] px-2.5 py-1 rounded-md border transition-colors ${
              on
                ? "bg-[#C5A059] text-[#060709] border-[#C5A059]"
                : "border-[#22262E] text-[#A1A4AB] hover:border-[#C5A059]/40 hover:text-[#FDFBF7]"
            }`}
            data-testid={`${idPrefix}-day-${i}`}
          >
            {lbl}
          </button>
        );
      })}
    </div>
  );
}

export default function ScheduleManager() {
  const [slots, setSlots] = useState([]);
  const [tracks, setTracks] = useState([]);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState({
    label: "", time_chicago: "06:00", days_of_week: [0, 1, 2, 3, 4, 5, 6],
    track_id: "", active: true, notes: "",
  });
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingDraft, setEditingDraft] = useState(null);

  const load = async () => {
    setLoading(true);
    setErr("");
    try {
      const [s, t] = await Promise.all([
        api.get("/admin/prayer-slots"),
        api.get("/admin/tracks/all"),
      ]);
      setSlots(s.data || []);
      // Only show approved tracks in the picker.
      setTracks((t.data || []).filter((x) => x.status === "approved"));
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const trackById = useMemo(() => {
    const m = new Map();
    tracks.forEach((t) => m.set(t.id, t));
    return m;
  }, [tracks]);

  const create = async () => {
    setErr("");
    if (!draft.label.trim()) { setErr("Label is required"); return; }
    if (!/^\d{2}:\d{2}$/.test(draft.time_chicago)) { setErr("Time must be HH:MM"); return; }
    if (!draft.days_of_week.length) { setErr("Pick at least one day"); return; }
    setSaving(true);
    try {
      await api.post("/admin/prayer-slots", {
        label: draft.label.trim(),
        time_chicago: draft.time_chicago,
        days_of_week: draft.days_of_week,
        track_id: draft.track_id || null,
        active: draft.active,
        notes: draft.notes.trim() || null,
      });
      setDraft({ ...draft, label: "", notes: "" });
      load();
    } catch (e) {
      setErr(formatApiError(e));
    } finally { setSaving(false); }
  };

  const startEdit = (s) => {
    setEditingId(s.id);
    setEditingDraft({
      label: s.label || "",
      time_chicago: s.time_chicago,
      days_of_week: s.days_of_week || [],
      track_id: s.track_id || "",
      active: !!s.active,
      notes: s.notes || "",
    });
  };

  const saveEdit = async () => {
    if (!editingId || !editingDraft) return;
    setErr("");
    try {
      await api.patch(`/admin/prayer-slots/${editingId}`, {
        ...editingDraft,
        track_id: editingDraft.track_id || null,
        notes: editingDraft.notes.trim() || null,
      });
      setEditingId(null); setEditingDraft(null);
      load();
    } catch (e) { setErr(formatApiError(e)); }
  };

  const toggleActive = async (s) => {
    try {
      await api.patch(`/admin/prayer-slots/${s.id}`, { active: !s.active });
      load();
    } catch (e) { setErr(formatApiError(e)); }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete prayer slot "${s.label}"?`)) return;
    try {
      await api.delete(`/admin/prayer-slots/${s.id}`);
      load();
    } catch (e) { setErr(formatApiError(e)); }
  };

  return (
    <section className="card-surface p-5 md:p-6 space-y-5" data-testid="schedule-manager">
      <header className="flex items-center justify-between gap-3">
        <div>
          <div className="overline text-[#C5A059] text-[10px]">Phase B.3</div>
          <h2 className="font-serif text-[#FDFBF7] text-xl md:text-2xl tracking-tight flex items-center gap-2">
            <CalendarClock className="w-5 h-5 text-[#C5A059]" strokeWidth={1.5} />
            Schedule & Prayer Slots
          </h2>
          <p className="text-[#A1A4AB] text-xs mt-0.5">All times are America/Chicago. Track-attached slots auto-play at the next clean break once the slot fires.</p>
        </div>
      </header>

      {err && (
        <div className="text-red-300 text-xs flex items-center gap-2" data-testid="schedule-err">
          <AlertCircle className="w-3.5 h-3.5" strokeWidth={1.5} /> {err}
        </div>
      )}

      {/* New slot form */}
      <div className="rounded-lg border border-[#22262E] bg-[#0E1014]/60 p-4 space-y-3" data-testid="schedule-new-form">
        <div className="overline text-[10px] text-[#A1A4AB]">New slot</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <input
            placeholder="Label (e.g. Morning Prayer)"
            value={draft.label}
            onChange={(e) => setDraft({ ...draft, label: e.target.value })}
            className="input-lux text-sm"
            data-testid="schedule-new-label"
          />
          <input
            type="time"
            value={draft.time_chicago}
            onChange={(e) => setDraft({ ...draft, time_chicago: e.target.value })}
            className="input-lux text-sm"
            data-testid="schedule-new-time"
          />
        </div>
        <DayPicker
          value={draft.days_of_week}
          onChange={(d) => setDraft({ ...draft, days_of_week: d })}
          idPrefix="schedule-new"
        />
        <select
          value={draft.track_id}
          onChange={(e) => setDraft({ ...draft, track_id: e.target.value })}
          className="input-lux text-sm w-full"
          data-testid="schedule-new-track"
        >
          <option value="">— Label only (no audio) —</option>
          {tracks.map((t) => (
            <option key={t.id} value={t.id}>{t.title} — {t.artist}</option>
          ))}
        </select>
        <input
          placeholder="Notes (optional)"
          value={draft.notes}
          onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
          className="input-lux text-sm"
        />
        <div className="flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={() => setDraft({ ...draft, active: !draft.active })}
            className="text-xs text-[#A1A4AB] hover:text-[#FDFBF7] inline-flex items-center gap-1.5"
          >
            {draft.active ? <ToggleRight className="w-4 h-4 text-[#C5A059]" strokeWidth={1.5} /> : <ToggleLeft className="w-4 h-4" strokeWidth={1.5} />}
            {draft.active ? "Active" : "Paused"}
          </button>
          <button
            type="button"
            onClick={create}
            disabled={saving}
            data-testid="schedule-new-save"
            className="overline text-xs px-3 py-1.5 rounded-md bg-[#C5A059] text-[#060709] hover:bg-[#DCC286] disabled:opacity-50 inline-flex items-center gap-1.5"
          >
            <Plus className="w-3.5 h-3.5" strokeWidth={2} /> Add slot
          </button>
        </div>
      </div>

      {/* Existing slots */}
      <div className="space-y-2" data-testid="schedule-list">
        {loading ? (
          <div className="text-[#6C707A] text-sm">Loading slots…</div>
        ) : slots.length === 0 ? (
          <div className="text-[#6C707A] text-sm">No slots yet — add one above.</div>
        ) : slots.map((s) => {
          const isEditing = editingId === s.id;
          const t = s.track || (s.track_id ? trackById.get(s.track_id) : null);
          return (
            <div
              key={s.id}
              data-testid={`schedule-row-${s.id}`}
              className="rounded-lg border border-[#22262E] bg-[#0a0a14]/40 p-3 space-y-2"
            >
              {isEditing ? (
                <div className="space-y-2">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    <input
                      value={editingDraft.label}
                      onChange={(e) => setEditingDraft({ ...editingDraft, label: e.target.value })}
                      className="input-lux text-sm"
                    />
                    <input
                      type="time"
                      value={editingDraft.time_chicago}
                      onChange={(e) => setEditingDraft({ ...editingDraft, time_chicago: e.target.value })}
                      className="input-lux text-sm"
                    />
                  </div>
                  <DayPicker
                    value={editingDraft.days_of_week}
                    onChange={(d) => setEditingDraft({ ...editingDraft, days_of_week: d })}
                    idPrefix={`schedule-edit-${s.id}`}
                  />
                  <select
                    value={editingDraft.track_id}
                    onChange={(e) => setEditingDraft({ ...editingDraft, track_id: e.target.value })}
                    className="input-lux text-sm w-full"
                  >
                    <option value="">— Label only —</option>
                    {tracks.map((tr) => (
                      <option key={tr.id} value={tr.id}>{tr.title} — {tr.artist}</option>
                    ))}
                  </select>
                  <div className="flex justify-end gap-2">
                    <button onClick={() => { setEditingId(null); setEditingDraft(null); }} className="text-xs px-3 py-1.5 rounded-md border border-[#22262E] text-[#A1A4AB] hover:text-[#FDFBF7]">
                      <X className="w-3 h-3 inline" strokeWidth={2} /> Cancel
                    </button>
                    <button onClick={saveEdit} className="text-xs px-3 py-1.5 rounded-md bg-[#C5A059] text-[#060709] hover:bg-[#DCC286]" data-testid={`schedule-edit-save-${s.id}`}>
                      <Save className="w-3 h-3 inline" strokeWidth={2} /> Save
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[#FDFBF7] font-medium text-sm truncate">{s.label}</span>
                      <span className="overline text-[10px] text-[#C5A059] tabular-nums">{s.time_chicago}</span>
                      <span className="text-[10px] text-[#6C707A]">{(s.days_of_week || []).map((i) => DAY_LABELS[i]).join(" · ")}</span>
                      {!s.active && <span className="overline text-[9px] px-1.5 py-0.5 rounded bg-[#22262E] text-[#A1A4AB]">PAUSED</span>}
                    </div>
                    <div className="text-xs text-[#A1A4AB] mt-1">
                      {t ? <>Track · <span className="text-[#FDFBF7]">{t.title}</span> — {t.artist}</> : <span className="text-[#6C707A]">Label-only (no auto-play)</span>}
                    </div>
                    {s.notes && <div className="text-[11px] text-[#6C707A] mt-1">{s.notes}</div>}
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button onClick={() => toggleActive(s)} title={s.active ? "Pause" : "Activate"} className="p-1.5 rounded-md border border-[#22262E] text-[#A1A4AB] hover:text-[#FDFBF7]" data-testid={`schedule-toggle-${s.id}`}>
                      {s.active ? <ToggleRight className="w-4 h-4 text-[#C5A059]" strokeWidth={1.5} /> : <ToggleLeft className="w-4 h-4" strokeWidth={1.5} />}
                    </button>
                    <button onClick={() => startEdit(s)} className="p-1.5 rounded-md border border-[#22262E] text-[#A1A4AB] hover:text-[#FDFBF7]" data-testid={`schedule-edit-${s.id}`}>
                      <Check className="w-3.5 h-3.5" strokeWidth={1.5} />
                    </button>
                    <button onClick={() => remove(s)} className="p-1.5 rounded-md border border-red-400/30 text-red-300 hover:bg-red-500/10" data-testid={`schedule-delete-${s.id}`}>
                      <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}
